// Feature flags globales de la plataforma (WD-1016).
//
// Cada flag se resuelve combinando tres fuentes, en este orden de prioridad:
//   1. window.env (public/env.js) — env en EJECUCIÓN; override por despliegue
//      sin rebuild (kill-switch). Ej: window.env.FEATURE_WEBI = 'false'
//   2. Base de datos — PreviewFeatures.DefaultValue, administrado desde la
//      pantalla "Feature Flags" del área de administración
//   3. import.meta.env (VITE_FEATURE_*) — default del build/flavor
// Si ninguna fuente define el flag, queda HABILITADO (true), salvo que exista
// una env legacy (ej. VITE_USE_WEBI) que se respeta como último fallback.
//
// La clave de cada flag es el FeatureTitle de la BD normalizado
// (minúsculas, espacios → guiones), igual que usePreviewFeatures.
import { env } from 'env';

export type FeatureFlagKey =
  | 'webi'
  | 'geocoder'
  | 'mfa'
  | 'clases-de-servicio'
  | 'rutas-troncales';

interface FeatureFlagDef {
  runtimeEnvKey: string; // window.env
  buildEnvKey: string; // import.meta.env
  legacyEnvKey?: string; // env previa a este sistema, último fallback
  legacyTruthyValue?: string; // valor que la env legacy considera "habilitado"
}

export const FEATURE_FLAG_DEFS: Record<FeatureFlagKey, FeatureFlagDef> = {
  webi: {
    runtimeEnvKey: 'FEATURE_WEBI',
    buildEnvKey: 'VITE_FEATURE_WEBI',
    legacyEnvKey: 'VITE_USE_WEBI',
    legacyTruthyValue: '1'
  },
  geocoder: {
    runtimeEnvKey: 'FEATURE_GEOCODER',
    buildEnvKey: 'VITE_FEATURE_GEOCODER'
  },
  mfa: {
    runtimeEnvKey: 'FEATURE_MFA',
    buildEnvKey: 'VITE_FEATURE_MFA'
  },
  'clases-de-servicio': {
    runtimeEnvKey: 'FEATURE_SERVICE_CLASSES',
    buildEnvKey: 'VITE_FEATURE_SERVICE_CLASSES'
  },
  'rutas-troncales': {
    runtimeEnvKey: 'FEATURE_TRUNK_ROUTES',
    buildEnvKey: 'VITE_FEATURE_TRUNK_ROUTES'
  }
};

// Interpreta el valor de una fuente: null/undefined = "no definido" (pasa a la
// siguiente fuente); acepta true/false, 1/0 y on/off en cualquier capitalización
const parseFlag = (raw: unknown): boolean | null => {
  if (raw == null || raw === '') return null;
  const value = String(raw).toLowerCase();
  if (['true', '1', 'on'].includes(value)) return true;
  if (['false', '0', 'off'].includes(value)) return false;
  return null;
};

/**
 * Resuelve un feature flag con las tres fuentes.
 * @param dbValue valor de BD (PreviewFeatures: Value del usuario ?? DefaultValue),
 *                null cuando no se conoce (p. ej. antes de iniciar sesión)
 */
export const resolveFeatureFlag = (key: FeatureFlagKey, dbValue?: string | null): boolean => {
  const def = FEATURE_FLAG_DEFS[key];
  if (!def) return true;

  // 1. env en ejecución (window.env via public/env.js)
  const runtime = parseFlag((window as any).env?.[def.runtimeEnvKey]);
  if (runtime !== null) return runtime;

  // 2. base de datos (pantalla Feature Flags)
  const db = parseFlag(dbValue);
  if (db !== null) return db;

  // 3. env de build (VITE_FEATURE_*)
  const build = parseFlag((env as any)[def.buildEnvKey]);
  if (build !== null) return build;

  // 4. env legacy (comportamiento previo a los feature flags)
  if (def.legacyEnvKey) {
    const legacyRaw = (env as any)[def.legacyEnvKey];
    if (legacyRaw != null && legacyRaw !== '') {
      return def.legacyTruthyValue
        ? String(legacyRaw) === def.legacyTruthyValue
        : parseFlag(legacyRaw) ?? true;
    }
  }

  return true;
};

// Caché de PreviewFeatures que mantiene usePreviewFeatures en localStorage.
const PREVIEW_FEATURES_STORAGE_KEY = 'userPreviewFeatures';

// Lee el valor de BD de un flag desde el caché de localStorage (valor del
// usuario ?? DefaultValue global), igual que usePreviewFeatures.getFeatureValue.
const getDbValueFromCache = (key: FeatureFlagKey): string | null => {
  try {
    const raw = localStorage.getItem(PREVIEW_FEATURES_STORAGE_KEY);
    if (!raw) return null;
    const features = JSON.parse(raw) as Array<{
      featureTitle?: string;
      value?: string | null;
      defaultValue?: string | null;
    }>;
    const feature = features.find(
      (f) => (f.featureTitle ?? '').toLowerCase().replace(/\s+/g, '-') === key
    );
    if (!feature) return null;
    return feature.value ?? feature.defaultValue ?? null;
  } catch {
    return null;
  }
};

/**
 * Versión NO-hook de useFeatureFlags().isFeatureFlagEnabled para usar en
 * contextos fuera de React (utils, sagas, funciones puras). Resuelve el flag
 * con las mismas fuentes/prioridad, leyendo la BD desde el caché de localStorage.
 */
export const isFeatureFlagEnabledStatic = (key: FeatureFlagKey): boolean =>
  resolveFeatureFlag(key, getDbValueFromCache(key));
